import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import PageMeta from '@/components/base/PageMeta';
import MovieCard from '@/components/feature/MovieCard';
import { movies, categories } from '@/mocks/movies';

export default function MovieDetail() {
  const { id } = useParams<{ id: string }>();

  const movie = useMemo(() => movies.find(m => m.id === id), [id]);
  const relatedMovies = useMemo(() => {
    if (!movie) return [];
    return movies.filter(m => m.category === movie.category && m.id !== movie.id).slice(0, 6);
  }, [movie]);

  if (!movie) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 mb-4">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </span>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">影片未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">该影片可能已下架或链接有误</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line"></i></span>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find(c => c.slug === movie.category);
  const title = `${movie.title} - 神马影院高清在线观看`;

  return (
    <>
      <PageMeta
        title={title}
        description={`${movie.title}（${movie.originalTitle}）${movie.year}年上映，导演${movie.director}，主演${movie.actors.slice(0, 3).join('、')}等。${movie.description.slice(0, 100)}...神马影院提供${movie.title}高清在线观看，即点即播，流畅不卡顿。`}
        canonical={`https://www.shenmayingyuan.com/movie/${movie.id}`}
        schemaScripts={[
          {
            "@context": "https://schema.org",
            "@type": "Movie",
            "name": movie.title,
            "description": movie.description,
            "image": movie.poster,
            "dateCreated": `${movie.year}`,
            "director": {
              "@type": "Person",
              "name": movie.director
            },
            "actor": movie.actors.map(a => ({
              "@type": "Person",
              "name": a
            })),
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": movie.rating.toString(),
              "bestRating": "10",
              "worstRating": "0"
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "首页",
                "item": "https://www.shenmayingyuan.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": category ? category.name : '影片',
                "item": `https://www.shenmayingyuan.com/category/${movie.category}`
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": movie.title,
                "item": `https://www.shenmayingyuan.com/movie/${movie.id}`
              }
            ]
          }
        ]}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16 min-h-screen">
        <article className="w-full px-4 md:px-6 pt-8 pb-12">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category?.name || '影片', href: `/category/${movie.category}` },
                { label: movie.title },
              ]}
            />

            <div className="flex flex-col md:flex-row gap-6 md:gap-8 mt-4">
              <div className="flex-shrink-0 w-full sm:w-[280px] md:w-[300px]">
                <div className="aspect-[2/3] rounded-lg overflow-hidden bg-background-200">
                  <img
                    src={movie.poster}
                    alt={movie.title}
                    className="w-full h-full object-cover object-top"
                    title={`${movie.title} - 神马影院高清在线观看`}
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-1">
                  {movie.title}
                </h1>
                <p className="text-sm text-foreground-500 mb-4">{movie.originalTitle}</p>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="bg-primary-500 text-background-50 text-sm font-bold px-3 py-1 rounded-md">
                    {movie.rating}
                  </span>
                  <span className="text-sm text-foreground-600">{movie.year}</span>
                  <span className="text-foreground-300">|</span>
                  <span className="text-sm text-foreground-600">{movie.duration}</span>
                  <span className="text-foreground-300">|</span>
                  <span className="text-sm text-foreground-600">{movie.language}</span>
                  <span className="text-foreground-300">|</span>
                  <span className="text-sm text-foreground-600">{movie.region}</span>
                  {category && (
                    <>
                      <span className="text-foreground-300">|</span>
                      <Link
                        to={`/category/${category.slug}`}
                        className="text-sm text-accent-600 hover:text-accent-700 font-medium cursor-pointer"
                      >
                        {category.name}
                      </Link>
                    </>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6 mb-6 text-sm">
                  <div className="flex gap-2">
                    <span className="text-foreground-500 font-medium whitespace-nowrap">导演：</span>
                    <span className="text-foreground-800">{movie.director}</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-foreground-500 font-medium whitespace-nowrap">主演：</span>
                    <span className="text-foreground-800">{movie.actors.join('、')}</span>
                  </div>
                </div>

                <div className="mb-6">
                  <h2 className="text-base font-heading font-bold text-foreground-900 mb-2">剧情简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{movie.description}</p>
                </div>

                <Link
                  to={`/category/${movie.category}`}
                  className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-2.5 rounded-full font-medium text-sm transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <span className="w-5 h-5 flex items-center justify-center"><i className="ri-play-circle-line"></i></span>
                  立即播放
                </Link>
              </div>
            </div>

            {relatedMovies.length > 0 && (
              <section className="mt-12 pt-10 border-t border-background-200/70">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900">
                    同类推荐
                  </h2>
                  <Link
                    to={`/category/${movie.category}`}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer"
                  >
                    查看更多
                  </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                  {relatedMovies.map(rm => {
                    const cat = categories.find(c => c.slug === rm.category);
                    return (
                      <MovieCard
                        key={rm.id}
                        id={rm.id}
                        title={rm.title}
                        year={rm.year}
                        category={rm.category}
                        rating={rm.rating}
                        poster={rm.poster}
                        categoryName={cat?.name}
                      />
                    );
                  })}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}